import React, { useEffect, useState } from 'react';
import { Modal, Form, Input } from 'antd';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { TBannerMgmt } from '../../../type';
import { ServerMeta } from 'types/common';

interface MedicationContentModalProps {
    visible: boolean;
    onCancel: () => void;
    onSave: (data: TBannerMgmt) => void;
    initialData?: TBannerMgmt;
    condition?: ServerMeta;
}

const BannerContentModal: React.FC<MedicationContentModalProps> = ({
    visible,
    onCancel,
    onSave,
    initialData,
    condition,
}) => {
    const [form] = Form.useForm<TBannerMgmt>();
    const [charCounts, setCharCounts] = useState({
        name: 0,
        subText: 0,
        callToActionName: 0,
    });

    // 更新字符计数
    const updateCharCounts = (data: Partial<TBannerMgmt>) => {
        setCharCounts({
            name: (data.name || '').length,
            subText: (data.subText || '').length,
            callToActionName: (data.callToActionName || '').length,
        });
    };

    // 当模态框打开或初始数据变化时，设置表单数据
    useEffect(() => {
        if (visible) {
            if (initialData) {
                form.setFieldsValue(initialData);
                updateCharCounts(initialData);
            } else {
                // 设置默认值
                const defaultValues = {
                    name: '',
                    subText: '',
                    callToActionName: '',
                };
                form.setFieldsValue(defaultValues);
                setCharCounts({
                    name: 0,
                    subText: 0,
                    callToActionName: 0,
                });
            }
        }
    }, [visible, initialData, form]);

    // 处理表单提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            onSave(values);
        } catch (error) {
            console.error('表单验证失败:', error);
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // 处理字段值变化
    const handleFieldChange = (fieldName: keyof TBannerMgmt, value: string) => {
        form.setFieldValue(fieldName, value);

        // 更新字符计数
        const newCounts = { ...charCounts };
        if (fieldName === 'name') {
            newCounts.name = value.length;
        } else if (fieldName === 'subText') {
            newCounts.subText = value.length;
        } else if (fieldName === 'callToActionName') {
            newCounts.callToActionName = value.length;
        }
        setCharCounts(newCounts);
    };

    return (
        <Modal
            title={`Edit ${condition?.content}`}
            className={commonS.modalFixHeightWrap}
            open={visible}
            onCancel={handleCancel}
            okText="Save changes"
            onOk={handleSubmit}
            width={543}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                className={s.form}
            >
                {/* Title Field */}
                <Form.Item
                    label="Title"
                    name="name"
                    rules={[
                        { required: true, message: 'Please enter title' },
                        { max: 200, message: 'Title cannot exceed 200 characters' },
                    ]}
                >
                    <Input
                        // placeholder="Wonder if ADHD medication is right for you?"
                        maxLength={200}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                    />
                </Form.Item>

                {/* Subtext Field */}
                <Form.Item
                    label="Subtext"
                    name="subText"
                    rules={[
                        { required: true, message: 'Please enter subtext' },
                        { max: 3000, message: 'Subtext cannot exceed 3000 characters' },
                    ]}
                >
                    <Input.TextArea
                        // placeholder="Connect with a licensed provider through Klarity Health and receive care in as little as 24 hours.* "
                        maxLength={3000}
                        rows={6}
                        onChange={(e) => handleFieldChange('subText', e.target.value)}
                        showCount
                    />
                </Form.Item>
                <Form.Item
                    label="Call-to-action name"
                    name="callToActionName"
                    rules={[
                        { required: true, message: 'Please enter call-to-action name' },
                        { max: 20, message: 'Call-to-action name cannot exceed 20 characters' },
                    ]}
                >
                    <Input
                        className={s.callToActionNameInput}
                        placeholder="Find a provider"
                        maxLength={20}
                        onChange={(e) => handleFieldChange('callToActionName', e.target.value)}
                    />
                </Form.Item>

                {/* Call to Action Link Field */}
                <Form.Item
                    label="Call-to-action link *"
                    name="callToActionLink"
                    rules={[
                        { required: true, message: 'Please enter call-to-action link' },
                        { type: 'url', message: 'Please enter a valid URL' },
                    ]}
                >
                    <Input
                        className={s.callToActionLinkInput}
                        placeholder="https://enroll-ins.helloklarity.com/adhd-v2?step=1"
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default BannerContentModal;
